import { request as routeRequest } from "@wildlark/relay-client"
import { connectNestConnection, createNestClient } from "../nest/client"
import type { AuthGrant } from "../nest/generated"
import { blindbindRoute, normalizeNestRoutes, nestRouteKey, shareableNestRoutes, type NestRoute } from "../nest/routes"
import { type GrantSummary, type PersonLevel, isPersonLevel, summarizeGrants } from "./people"
import type { AddRemoteComputerInput } from "./types"

const PAIRING_TIMEOUT_MS = 8_000
const PAIRING_HANDOFF_ORIGIN = "https://app.wildlark.ai"
const PAIRING_HANDOFF_PATH = "/pair"

/**
 * A share invite is an ordinary pairing invite whose code carries scoped
 * grants on the node, plus the inviter's non-secret introduction so the
 * accepting client can show who shared what before the first connection.
 */
export interface PairingInviteShare {
    from: string
    level: PersonLevel
    glyph?: string
    color?: string
    project?: string
}

export interface PairingInvite {
    routes: NestRoute[]
    code: string
    nodeId: string
    pushClaim?: string
    share?: PairingInviteShare
}

/** The client's own introduction sent with /v1/pair when accepting a share. */
export interface PairingClientProfile {
    name?: string
    glyph?: string
    color?: string
}

export interface PairedAccess {
    principalId?: string
    kind: string
    displayName: string
    summary: GrantSummary
    grants: AuthGrant[]
}

export interface PairedComputer extends AddRemoteComputerInput {
    access: PairedAccess
    nodeName: string
}

export function deviceNameForUserAgent(userAgent: string, platform: string): string {
    const browser = /(?:Edg|EdgiOS|EdgA)\//.test(userAgent)
        ? "Edge"
        : /(?:CriOS|Chrome)\//.test(userAgent)
          ? "Chrome"
          : /(?:FxiOS|Firefox)\//.test(userAgent)
            ? "Firefox"
            : /Version\/[^ ]+.*Safari\//.test(userAgent)
              ? "Safari"
              : "Browser"
    const device = /iPad/.test(userAgent)
        ? "iPad"
        : /iPhone/.test(userAgent)
          ? "iPhone"
          : /Android/.test(userAgent)
            ? "Android"
            : /Windows/.test(userAgent) || platform.startsWith("Win")
              ? "Windows"
              : /Macintosh|Mac OS X/.test(userAgent) || platform.startsWith("Mac")
                ? "Mac"
                : /CrOS/.test(userAgent)
                  ? "ChromeOS"
                  : /Linux/.test(userAgent) || /Linux/.test(platform)
                    ? "Linux"
                    : platform.trim() || "device"
    return `${browser} on ${device}`
}

export function currentBrowserDeviceName(): string {
    return deviceNameForUserAgent(navigator.userAgent, navigator.platform)
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function encodeRoutes(routes: NestRoute[]): string {
    const bytes = new TextEncoder().encode(JSON.stringify(routes))
    let binary = ""
    for (const byte of bytes) binary += String.fromCharCode(byte)
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "")
}

function decodeRoutes(value: string): NestRoute[] {
    if (value.length > 8_192 || !/^[A-Za-z0-9_-]+$/.test(value)) throw new Error("Pairing routes are invalid")
    const padded = value
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(Math.ceil(value.length / 4) * 4, "=")
    const binary = atob(padded)
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
    return normalizeNestRoutes(JSON.parse(new TextDecoder().decode(bytes)) as unknown)
}

export function createPairingInviteUrl({ routes, code, nodeId, pushClaim, share }: PairingInvite): string {
    const shareableRoutes = shareableNestRoutes(normalizeNestRoutes(routes))
    const pairingCode = code.trim()
    const expectedNodeId = nodeId.trim()
    if (shareableRoutes.length === 0) throw new Error("Pairing requires an HTTPS direct route or hosted relay route")
    if (!pairingCode || !expectedNodeId) throw new Error("A pairing code and computer identity are required")
    const params = new URLSearchParams({
        v: "2",
        routes: encodeRoutes(shareableRoutes),
        code: pairingCode,
        node: expectedNodeId,
    })
    const enrollmentClaim = pushClaim?.trim()
    if (enrollmentClaim) params.set("push", enrollmentClaim)
    if (share) {
        const from = share.from.trim()
        if (!from) throw new Error("A share invite needs the sharer's name")
        params.set("share", share.level)
        params.set("from", from)
        if (share.glyph?.trim()) params.set("glyph", share.glyph.trim())
        if (share.color?.trim()) params.set("color", share.color.trim())
        if (share.project?.trim()) params.set("project", share.project.trim())
    }
    return `${PAIRING_HANDOFF_ORIGIN}${PAIRING_HANDOFF_PATH}#${params.toString()}`
}

export function readPairingInvite(value: string): PairingInvite | null {
    let parsed: URL
    try {
        parsed = new URL(value)
    } catch {
        return null
    }
    const canonicalHandoff = parsed.origin === PAIRING_HANDOFF_ORIGIN && parsed.pathname.replace(/\/$/, "") === PAIRING_HANDOFF_PATH
    if (!canonicalHandoff || parsed.search) return null
    const params = new URLSearchParams(parsed.hash.slice(1))
    if (params.get("v") !== "2") return null
    const code = params.get("code")?.trim()
    const nodeId = params.get("node")?.trim()
    const encodedRoutes = params.get("routes")?.trim()
    if (!code || !nodeId || !encodedRoutes) return null
    try {
        const routes = shareableNestRoutes(decodeRoutes(encodedRoutes))
        if (routes.length === 0) return null
        const pushClaim = params.get("push")?.trim()
        const share = readInviteShare(params)
        return { routes, code, nodeId, ...(pushClaim ? { pushClaim } : {}), ...(share ? { share } : {}) }
    } catch {
        return null
    }
}

function readInviteShare(params: URLSearchParams): PairingInviteShare | null {
    const level = params.get("share")?.trim()
    const from = params.get("from")?.trim()
    if (!level || !from || !isPersonLevel(level)) return null
    const glyph = params.get("glyph")?.trim()
    const color = params.get("color")?.trim()
    const project = params.get("project")?.trim()
    return { from, level, ...(glyph ? { glyph } : {}), ...(color ? { color } : {}), ...(project ? { project } : {}) }
}

async function responseError(response: Response): Promise<string> {
    try {
        const value: unknown = await response.json()
        if (isRecord(value) && typeof value.error === "string") return value.error
    } catch {
        // Use the status below.
    }
    return response.statusText || `HTTP ${response.status}`
}

async function requestWithTimeout(route: NestRoute, path: string, init: RequestInit = {}, timeoutMs = PAIRING_TIMEOUT_MS): Promise<Response> {
    const controller = new AbortController()
    const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs)
    try {
        return await routeRequest(blindbindRoute(route), path, { ...init, signal: controller.signal })
    } catch (error) {
        if (controller.signal.aborted) throw new Error("The computer did not respond in time")
        throw error
    } finally {
        globalThis.clearTimeout(timeout)
    }
}

async function selectPairingRoute(routes: NestRoute[], expectedNodeId?: string): Promise<{ route: NestRoute; nodeId: string }> {
    let lastError: unknown
    for (let index = 0; index < routes.length; index += 1) {
        const route = routes[index]
        if (!route) continue
        try {
            const healthResponse = await requestWithTimeout(route, "/v1/health", {}, index < routes.length - 1 ? 1_500 : PAIRING_TIMEOUT_MS)
            if (!healthResponse.ok) throw new Error(`Could not reach this computer: ${await responseError(healthResponse)}`)
            const health: unknown = await healthResponse.json()
            if (!isRecord(health) || typeof health.nodeId !== "string" || !health.nodeId) throw new Error("This route is not an WildLark computer")
            if (expectedNodeId && health.nodeId !== expectedNodeId) throw new Error("This pairing link belongs to a different WildLark computer")
            return { route, nodeId: health.nodeId }
        } catch (error) {
            lastError = error
        }
    }
    throw lastError instanceof Error ? lastError : new Error("No pairing route could reach this computer")
}

function parseGrants(value: unknown): AuthGrant[] {
    if (!Array.isArray(value)) return []
    const grants: AuthGrant[] = []
    for (const item of value) {
        if (!isRecord(item) || !isRecord(item.scope) || typeof item.capability !== "string" || typeof item.scope.type !== "string") continue
        const capability = item.capability
        if (capability !== "read" && capability !== "write" && capability !== "exec" && capability !== "agent" && capability !== "admin") continue
        grants.push({
            capability,
            scope: { type: item.scope.type, ...(typeof item.scope.workspaceId === "string" ? { workspaceId: item.scope.workspaceId } : {}) },
            ...(typeof item.expiresAt === "string" ? { expiresAt: item.expiresAt } : {}),
        })
    }
    return grants
}

/** "Parham's Mac" beats "Parham's Parhams-MacBook-Air.local" in every list. */
export function pairedComputerName(nodeName: string, nodeId: string, share?: PairingInviteShare): string {
    const base = nodeName.trim() || nodeId
    if (!share) return base
    const hostnameLike = /^[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*$/.test(base) && (base.includes("-") || base.includes("."))
    if (!hostnameLike) return `${share.from}'s ${base}`
    const lower = base.toLowerCase()
    const kind = lower.includes("macbook") || lower.includes("imac") || lower.includes("mac") ? "Mac" : "computer"
    return `${share.from}'s ${kind}`
}

export async function pairWithComputer({
    routes,
    code,
    clientName,
    expectedNodeId,
    profile,
    share,
}: {
    routes: NestRoute[]
    code: string
    clientName: string
    expectedNodeId?: string
    profile?: PairingClientProfile
    share?: PairingInviteShare
}): Promise<PairedComputer> {
    const normalizedRoutes = shareableNestRoutes(normalizeNestRoutes(routes))
    if (normalizedRoutes.length === 0) throw new Error("Pairing requires a shareable route")
    const pairingCode = code.trim()
    if (!pairingCode) throw new Error("Enter the pairing code shown on the computer")

    const selected = await selectPairingRoute(normalizedRoutes, expectedNodeId)
    const selectedKey = nestRouteKey(selected.route)
    const connectedRoutes = [selected.route, ...normalizedRoutes.filter((route) => nestRouteKey(route) !== selectedKey)]
    const meta: Record<string, string> = {}
    if (profile?.glyph?.trim()) meta.glyph = profile.glyph.trim()
    if (profile?.color?.trim()) meta.color = profile.color.trim()
    const client = profile?.name?.trim() || Object.keys(meta).length > 0 ? { name: profile?.name?.trim() ?? "", meta } : undefined
    const pairResponse = await requestWithTimeout(selected.route, "/v1/pair", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: pairingCode, clientName, ...(client ? { client } : {}) }),
    })
    if (!pairResponse.ok) throw new Error(`Pairing failed: ${await responseError(pairResponse)}`)
    const paired: unknown = await pairResponse.json()
    if (!isRecord(paired) || typeof paired.token !== "string" || !paired.token) throw new Error("Pairing returned an invalid device token")

    const probe = createNestClient()
    try {
        await connectNestConnection(probe, { nodeId: selected.nodeId, routes: connectedRoutes, token: paired.token }, { timeoutMs: PAIRING_TIMEOUT_MS })
        // system.initialize is the one call every principal may make, so it is
        // the identity check for full devices and scoped shares alike.
        const [initialized, principal] = await Promise.all([
            probe.requestMethod("system.initialize", { clientName, protocolVersion: 1 }, { timeoutMs: PAIRING_TIMEOUT_MS }),
            probe.requestMethod("auth.principal.read", {}, { timeoutMs: PAIRING_TIMEOUT_MS }).catch(() => null),
        ])
        if (initialized.nodeId !== selected.nodeId) throw new Error("The paired computer identity changed during setup")
        const responseTokenId = typeof paired.tokenId === "string" && paired.tokenId.trim() ? paired.tokenId.trim() : null
        const principalTokenId = isRecord(principal) && typeof principal.tokenId === "string" && principal.tokenId.trim() ? principal.tokenId.trim() : null
        const tokenId = responseTokenId ?? principalTokenId
        const grants = parseGrants(isRecord(principal) ? principal.grants : paired.grants)
        const nodeName = initialized.nodeName || selected.nodeId
        return {
            name: pairedComputerName(nodeName, selected.nodeId, share),
            nodeName,
            nodeId: selected.nodeId,
            routes: connectedRoutes,
            token: paired.token,
            ...(tokenId ? { tokenId } : {}),
            access: {
                ...(typeof paired.principalId === "string" && paired.principalId ? { principalId: paired.principalId } : {}),
                kind: typeof paired.kind === "string" ? paired.kind : "device",
                displayName: typeof paired.displayName === "string" ? paired.displayName : "",
                grants,
                summary: summarizeGrants(grants, share?.level === "propose"),
            },
        }
    } finally {
        probe.close()
    }
}
